import { EavAttributeValueTypeEnum } from '@zaoblako/shared/modules/eav/enums/eav-attribute-value-type.enum';
import { DtoMessageEnum } from '@zaoblako/shared/common/enums/dto.message.enum';
import { BadRequestException } from '@zaoblako/shared/common/exceptions/bad-request.exception';
import { InternalServerErrorException } from '@zaoblako/shared/common/exceptions/internal-server-error.exception';
import type { EavAttributeFindInterface } from '@zaoblako/shared/modules/eav/dtos/attribute/eav-attribute-find.dto';
import type { HelpdeskTaskStatusFindInterface } from '@zaoblako/shared/modules/helpdesk/dto/helpdesk-task-status-find.dto';
import type { BaseHttpException } from '@zaoblako/shared/common/exceptions/base-http.exception';
import type { SerdeDtoMetadataInterface } from '@zaoblako/shared/modules/serde/interfaces/serde-dto-metadata.interface';

import * as yup from 'yup';
import { unflatten } from 'flat';
import { lowerFirst, identity } from 'lodash';

/**
 * Validation error transformer function: receives origin validation errors and return their transformed version.
 * Need to make EAV entity validation error part of parent entity error (e.g. HD task or HD task template).
 */
export interface ValidationErrorTransformer {
  (validationErrors: Record<number, string>): any;
}

/**
 * Validation metadata transformer function: receives origin validation metadata and return it's transformed version.
 * Need to make EAV entity validation metadata part of parent entity metadata (e.g. HD task or HD task template).
 */
export interface ValidationMetadataTransformer {
  (validationMetadata: SerdeDtoMetadataInterface<Record<number, any>>): any;
}

/** Common validator creation options */
export interface ValidatorCreationOptions {
  /** If defined, then mandatory attribute value will be computed by this HD status ID */
  hdTaskStatus?: HelpdeskTaskStatusFindInterface;
  /**
   * Validation error transformer function: receives origin validation errors and return their transformed version.
   * Need to make EAV entity validation error part of parent entity error (e.g. HD task or HD task template).
   */
  transformValidationErrors?: ValidationErrorTransformer;
  /**
   * Validation metadata transformer function: receives origin validation metadata and return it's transformed version.
   * Need to make EAV entity validation metadata part of parent entity metadata (e.g. HD task or HD task template).
   */
  transformValidationMetadata?: ValidationMetadataTransformer;
  /** Additional async validators for attribute value types. Using only by async validators. */
  asyncAttributeValueValidators?: Partial<Record<EavAttributeValueTypeEnum, yup.TestFunction>>;
}

/**
 * A safe sync validator: function which received source attribute values and returns a tuple where:
 *   - The first argument is validated value of `null` if validation failed;
 *   - The second argument is validation error or `null` if validation succeed;
 */
export interface SafeSyncValidator {
  (value: Record<number, any>): [Record<number, any>, BaseHttpException];
}

/**
 * A safe async validator: function which received source attribute values and returns a `Promise` with a tuple where:
 *   - The first argument is validated value of `null` if validation failed;
 *   - The second argument is validation error or `null` if validation succeed;
 */
export interface SafeAsyncValidator {
  (value: Record<number, any>): Promise<[Record<number, any>, BaseHttpException]>;
}

/**
 * An unsafe sync validator: function which received source attribute values and:
 *   - Returns validated value if validation succeed;
 *   - Throws exception if validation failed;
 */
export interface UnsafeSyncValidator {
  (value: Record<number, any>): Record<number, any>;
}

/**
 * An unsafe async validator: function which received source attribute values and:
 *   - Returns `Promise` with validated value if validation succeed;
 *   - Throws exception if validation failed;
 */
export interface UnsafeAsyncValidator {
  (value: Record<number, any>): Promise<Record<number, any>>;
}

/** {@link EntityValidatorClass.createAttributeValidator} method parameters */
interface CreateAttributeValidatorParams extends ValidatorCreationOptions {
  /** EAV attribute descriptior */
  attribute: EavAttributeFindInterface;
  /** If `true`, then backend async tests will be added (if any) */
  addAsyncTest?: boolean;
}

/** Provides isomorphic validation service for EAV entities. Singleton object. */
export class EntityValidatorClass {
  private static instance: EntityValidatorClass = null;

  /** Get validation service instance. Create it if not exists. */
  public static getInstance(): EntityValidatorClass {
    if (!EntityValidatorClass.instance) {
      EntityValidatorClass.instance = new EntityValidatorClass();
    }
    return EntityValidatorClass.instance;
  }

  /**
   * Create SerDe metadata for object constucted from received attributes
   *
   * @param attributes EAV attributes descriptiors
   * @param [options] Additional options
   *
   * @returns SerDe metadata
   */
  public createMetadata(attributes: EavAttributeFindInterface[], options?: ValidatorCreationOptions): SerdeDtoMetadataInterface<Record<number, any>> {
    return Object.fromEntries(attributes.map(item => {
      let required = false;
      if (options?.hdTaskStatus?.id && item.requiredOnHdTaskStatuses?.length) {
        required = item.requiredOnHdTaskStatuses.some(item => item.id === options?.hdTaskStatus.id);
      }
      return [
        item.id,
        {
          label: item.name,
          optional: !required,
        },
      ];
    }));
  }

  /**
   * Create safe sync validator for object constucted from received attributes
   *
   * @param attributes EAV attributes descriptiors
   * @param [options] Additional options
   *
   * @returns A safe validator: function which received source attribute values and returns a typle where:
   *   - The first argument is validated value of `null` if validation failed;
   *   - The second argument is validation error or `null` if validation succeed;
   */
  public createSafeSyncValidator = (attributes: EavAttributeFindInterface[], options?: ValidatorCreationOptions): SafeSyncValidator => {
    const schema = yup.object(Object.fromEntries(attributes.map(attribute => [
      attribute.id,
      this.createAttributeValidator({
        ...(options || {}),
        attribute,
      }),
    ])));

    const transformValidationErrors: ValidationErrorTransformer = options?.transformValidationErrors || identity;
    const transformValidationMetadata: ValidationMetadataTransformer = options?.transformValidationMetadata || identity;

    return (value: Record<number, any>): [Record<number, any>, BaseHttpException] => {
      let result: Record<number, any> = null;
      let error: BaseHttpException = null;

      try {
        result = schema.validateSync(value);
      } catch (err) {
        if (err instanceof yup.ValidationError) {
          error = new BadRequestException('Ошибка валидации');
          error.validationMetadata = transformValidationMetadata(this.createMetadata(attributes, options));
          error.validationErrors = transformValidationErrors(unflatten({
            [(<yup.ValidationError>err).path]: (<yup.ValidationError>err).errors.join('\n')
          }));
        } else {
          error = new InternalServerErrorException(err?.message || String(err), { cause: err });
        }
      }

      return [result, error];
    };
  };

  /**
   * Create safe async validator for object constucted from received attributes
   *
   * @param attributes EAV attributes descriptiors
   * @param [options] Additional options
   *
   * @returns A safe validator: function which received source attribute values and returns a typle where:
   *   - The first argument is validated value of `null` if validation failed;
   *   - The second argument is validation error or `null` if validation succeed;
   */
  public createSafeAsyncValidator = (attributes: EavAttributeFindInterface[], options?: ValidatorCreationOptions): SafeAsyncValidator => {
    const schema = yup.object(Object.fromEntries(attributes.map(attribute => [
      attribute.id,
      this.createAttributeValidator({
        ...(options || {}),
        attribute,
        addAsyncTest: true,
      }),
    ])));

    const transformValidationErrors: ValidationErrorTransformer = options?.transformValidationErrors || identity;
    const transformValidationMetadata: ValidationMetadataTransformer = options?.transformValidationMetadata || identity;

    return async (value: Record<number, any>): Promise<[Record<number, any>, BaseHttpException]> => {
      let result: Record<number, any> = null;
      let error: BaseHttpException = null;

      try {
        result = await schema.validate(value);
      } catch (err) {
        if (err instanceof yup.ValidationError) {
          error = new BadRequestException('Ошибка валидации');
          error.validationMetadata = transformValidationMetadata(this.createMetadata(attributes, options));
          error.validationErrors = transformValidationErrors(unflatten({
            [(<yup.ValidationError>err).path]: (<yup.ValidationError>err).errors.join('\n')
          }));
        } else {
          error = new InternalServerErrorException(err?.message || String(err), { cause: err });
        }
      }

      return [result, error];
    };
  };

  /**
   * Create unsafe sync validator for object constucted from received attributes
   *
   * @param attributes EAV attributes descriptiors
   * @param [options] Additional options
   *
   * @returns An unsafe validator: function which received source attribute values and:
   *   - Returns validated value if validation succeed;
   *   - Throws exception if validation failed;
   */
  public createUnsafeSyncValidator = (attributes: EavAttributeFindInterface[], options?: ValidatorCreationOptions): UnsafeSyncValidator => {
    const safeValidator = this.createSafeSyncValidator(attributes, options);
    return (value: Record<number, any>): Record<number, any> => {
      const [result, error] = safeValidator(value);
      if (error) {
        throw error;
      }
      return result;
    };
  };

  /**
   * Create unsafe async validator for object constucted from received attributes
   *
   * @param attributes EAV attributes descriptiors
   * @param [hdTaskStatus] If defined, then mandatory attribute value will be computed by this HD status ID
   *
   * @returns An unsafe validator: function which received source attribute values and:
   *   - Returns validated value if validation succeed;
   *   - Throws exception if validation failed;
   */
  public createUnsafeAsyncValidator = (attributes: EavAttributeFindInterface[], options?: ValidatorCreationOptions): UnsafeAsyncValidator => {
    const safeValidator = this.createSafeAsyncValidator(attributes, options);
    return async (value: Record<number, any>): Promise<Record<number, any>> => {
      const [result, error] = await safeValidator(value);
      if (error) {
        throw error;
      }
      return result;
    };
  };

  /**
   * Create attribute validator
   *
   * @param params0 Method parameters
   *
   * @returns `yup` validation schema
   */
  private createAttributeValidator({
    attribute,
    hdTaskStatus,
    addAsyncTest,
    asyncAttributeValueValidators,
  }: CreateAttributeValidatorParams): yup.Schema<any> {
    let required = false;
    let requiredMessage: string;
    if (hdTaskStatus?.id && attribute.requiredOnHdTaskStatuses?.length) {
      required = attribute.requiredOnHdTaskStatuses.some(item => item.id === hdTaskStatus.id);
      requiredMessage = `Обязательно для заявок со статусом "${hdTaskStatus.name}"`;
    }

    let result: yup.Schema<any>;
    switch (attribute.type) {
      case EavAttributeValueTypeEnum.BOOLEAN: {
        result = yup.boolean();
        if (!required) {
          result = result
            .nullable()
            .notRequired();
        } else {
          result = result
            .required(requiredMessage)
            .nonNullable(requiredMessage);
        }
        break;
      }
      case EavAttributeValueTypeEnum.NUMBER: {
        result = yup.number();
        if (!required) {
          result = result
            .nullable()
            .notRequired();
        } else {
          result = result
            .required(requiredMessage)
            .nonNullable(requiredMessage);
        }
        break;
      }
      case EavAttributeValueTypeEnum.TEXT: {
        result = yup.string();
        if (!required) {
          result = result
            .nullable()
            .notRequired();
        } else {
          result = result
            .required(requiredMessage)
            .nonNullable(requiredMessage)
            .min(1, `${DtoMessageEnum.EMPTY_STRING} - ${lowerFirst(requiredMessage)}`);
        }
        break;
      }
      case EavAttributeValueTypeEnum.DATE:
      case EavAttributeValueTypeEnum.TIMESTAMP: {
        result = yup.date();
        if (!required) {
          result = result
            .nullable()
            .notRequired();
        } else {
          result = result
            .required(requiredMessage)
            .nonNullable(requiredMessage);
        }
        break;
      }
      case EavAttributeValueTypeEnum.DATE_PERIOD: {
        result = yup.object({
          from: yup
            .date()
            .required()
            .nonNullable(),
          to: yup
            .date()
            .required()
            .nonNullable(),
        });
        if (!required) {
          result = result
            .nullable()
            .notRequired();
        } else {
          result = result
            .required(requiredMessage)
            .nonNullable(requiredMessage);
        }
        break;
      }
      case EavAttributeValueTypeEnum.FILES: {
        result = yup.array(yup.object({
          id: yup
            .number()
            .integer(DtoMessageEnum.NOT_INTEGER)
            .positive(DtoMessageEnum.NOT_POSITIVE)
            .required()
            .nonNullable(),
        }));
        if (addAsyncTest && asyncAttributeValueValidators[attribute.type]) {
          result = result.test({ test: asyncAttributeValueValidators[attribute.type] });
        }
        if (!required) {
          result = result
            .nullable()
            .notRequired();
        } else {
          result = result
            .required(requiredMessage)
            .nonNullable(requiredMessage)
            .min(1, `${DtoMessageEnum.EMPTY_ARRAY} - ${lowerFirst(requiredMessage)}`);
        }
        break;
      }
      case EavAttributeValueTypeEnum.FILIAL:
      case EavAttributeValueTypeEnum.COST_CENTER: {
        result = yup.object({
          id: yup
            .number()
            .integer(DtoMessageEnum.NOT_INTEGER)
            .positive(DtoMessageEnum.NOT_POSITIVE)
            .required()
            .nonNullable(),
        });
        if (attribute.multipleSelect) {
          result = yup.array(result);
          if (addAsyncTest && asyncAttributeValueValidators[attribute.type]) {
            result = result.test({ test: asyncAttributeValueValidators[attribute.type] });
          }
          if (!required) {
            result = result
              .nullable()
              .notRequired();
          } else {
            result = result
              .required(requiredMessage)
              .nonNullable(requiredMessage)
              .min(1, `${DtoMessageEnum.EMPTY_ARRAY} - ${lowerFirst(requiredMessage)}`);
          }
        } else {
          if (addAsyncTest && asyncAttributeValueValidators[attribute.type]) {
            result = result.test({ test: asyncAttributeValueValidators[attribute.type] });
          }
          if (!required) {
            result = result
              .nullable()
              .notRequired();
          } else {
            result = result
              .required(requiredMessage)
              .nonNullable(requiredMessage);
          }
        }
        break;
      }
    }

    return result;
  }
}
